import { AsyncPipe } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  Observable,
  of,
  race,
  skip,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';

import { ChatFacade } from '@blaze-dashboard/ai-chat-data-access';
import {
  BlazeTableComponent,
  BlazeTableModule,
  TableDataGenerationPanelComponent,
} from '@blaze-dashboard/blaze-table-feature';
import {
  ComparisonQueryNode,
  EnvironmentName,
  LogicalQueryNode,
  LogicalType,
  QueryNodeType,
  SystemTableType,
} from '@blaze-shared/api-contracts-domain';
import {
  featureFlagsSelectors,
  ProjectContextFacade,
  projectContextSelectors,
} from '@blaze-shared/app-state-data-access';
import { AuthService } from '@blaze-shared/auth-data-access';
import {
  DatasetsApiService,
  TablesApiService,
  TablesCollectionService,
} from '@blaze-shared/datasets-data-access';
import {
  BlazeTable,
  convertBlazeTableSchemaToBlazeTable,
  convertBlazeTableToTableInfo,
  DEFAULT_BLAZE_TABLE,
} from '@blaze-shared/datasets-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { DatasetRowsService } from '@blaze-shared/schema-data-access';
import { DatasetRow } from '@blaze-shared/schema-domain';
import {
  DialogModule,
  DialogService,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ButtonIconComponent,
  PanelComponent,
  TooltipDirective,
} from '@blaze-shared/shared-ui';
import { isDefined } from '@blaze-shared/shared-util';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { DataConnectorsStore } from '../../+state/data-connectors.store';
import {
  TableEditorChatContextProvider,
  type TableEditorChatContextSession,
} from '../../services/table-editor-chat-context.provider';

import {
  BlazeTableCopyDataDialogComponent,
  CopyDataDialogInput,
} from './blaze-table-copy-data-dialog/blaze-table-copy-data-dialog.component';
import { DuplicateBlazeTableDialogComponent } from './blaze-table-duplicate-dialog/blaze-table-duplicate-dialog.component';
import {
  BlazeTableFilterDialogComponent,
  BlazeTableFilterDialogInput,
} from './blaze-table-filter-dialog/blaze-table-filter-dialog.component';
import { BlazeTableIndexesDialogComponent } from './blaze-table-indexes-dialog/blaze-table-indexes-dialog.component';
import { BlazeTableTriggersButtonComponent } from './blaze-table-triggers-button/blaze-table-triggers-button.component';
import {
  BlazeTableTriggersDialogComponent,
  BlazeTableTriggersDialogData,
  BlazeTableTriggersDialogResult,
} from './blaze-table-triggers-dialog/blaze-table-triggers-dialog.component';

@UntilDestroy()
@Component({
  selector: 'blaze-table-dialog',
  templateUrl: './blaze-table-dialog.component.html',
  styleUrls: ['./blaze-table-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    BlazeTableModule,
    BlazeTableTriggersButtonComponent,
    ButtonBarComponent,
    ButtonIconComponent,
    DialogModule,
    EnvironmentIndicatorComponent,
    FormsModule,
    IconsModule,
    MatMenuModule,
    PanelComponent,
    TableDataGenerationPanelComponent,
    TooltipDirective,
  ],
  host: { class: 'data-connector-dialog' },
})
export class BlazeTableDialogComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly chatFacade = inject(ChatFacade);
  private readonly dataConnectorsStore = inject(DataConnectorsStore);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly dialog = inject(MatDialog);
  private readonly dialogService = inject(DialogService);
  private readonly injector = inject(Injector);
  private readonly notificationService = inject(NotificationService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly store = inject(Store);
  private readonly tableEditorChatContextProvider = inject(TableEditorChatContextProvider);
  private readonly tablesApiService = inject(TablesApiService);
  private readonly tablesCollService = inject(TablesCollectionService);
  elRef = inject(ElementRef);

  @Output() refreshDataset = new EventEmitter();
  @Output() showPrevPage = new EventEmitter();

  @ViewChild('blazeTable') blazeTable!: BlazeTableComponent;
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('openMenuButton') openMenuButton!: ElementRef<HTMLButtonElement>;

  private chatContextSession?: TableEditorChatContextSession;
  private triggersDialogRef?: MatDialogRef<
    BlazeTableTriggersDialogComponent,
    BlazeTableTriggersDialogResult
  >;
  private filterDialogRef?: MatDialogRef<BlazeTableFilterDialogComponent>;
  private indexesDialogRef?: MatDialogRef<BlazeTableIndexesDialogComponent>;

  protected sourceEnvironment = signal<EnvironmentName | undefined>('Development');

  projectId = '';
  table$!: BehaviorSubject<{
    config: BlazeTable;
    data: DatasetRow[];
    pageToken: string | null;
    totalCount: number | null;
  }>;
  table!: BlazeTable; // blaze table configuration
  tableData: DatasetRow[] = []; // table data before last save
  filters = signal<LogicalQueryNode | undefined>(undefined);

  changeTrackingEnabled$: Observable<boolean>;
  isGeneratorEnabled$: Observable<boolean>;

  dataGenerationPanelVisible = false;
  dataGenerationConfig$ = new BehaviorSubject<{ config?: BlazeTable }>({});

  refreshInProgress = signal(false);
  isNotLinkedTable = signal(false);

  get isRootUser(): boolean {
    return this.auth.isCurrentUserAccountRoot();
  }

  get tableNameSize(): number {
    return Math.min(this.table.name.length, 50);
  }

  get canModify(): boolean {
    return (
      !this.table.isChangeTrackingTable &&
      this.table.systemTableType !== SystemTableType.InternalUsers &&
      this.table.systemTableType !== SystemTableType.UserActivityLog
    );
  }

  get csvImportLabel(): string {
    return this.tableData?.length ? 'Add rows from CSV' : 'Import from CSV';
  }

  constructor() {
    this.changeTrackingEnabled$ = this.store.select(featureFlagsSelectors.getFeatureFlags).pipe(
      first(),
      map((flags) => !!flags.ChangeTracking),
    );

    this.isGeneratorEnabled$ = combineLatest([
      this.store.select(featureFlagsSelectors.getFeatureFlags),
      this.store.select(projectContextSelectors.selectEnvironmentName),
    ]).pipe(
      first(),
      map(
        ([flags, env]) =>
          (flags.MultipleEnvironments && env === 'Development') || !flags.MultipleEnvironments,
      ),
    );
  }

  ngOnInit(): void {
    // Source environment is always the non-current environment.
    this.projectContextFacade.environmentName$.pipe(untilDestroyed(this)).subscribe((env) => {
      if (env === 'Development') {
        this.sourceEnvironment.set('Production');
      } else if (env === 'Production') {
        this.sourceEnvironment.set('Development');
      }
    });

    combineLatest([
      this.dataConnectorsStore.blazeTable$,
      this.dataConnectorsStore.blazeTablePageToken$,
      this.dataConnectorsStore.blazeTableTotalCount$,
      this.dataConnectorsStore.projectId$,
      this.dataConnectorsStore.previewData$,
    ])
      .pipe(take(1))
      .subscribe(([blazeTable, pageToken, totalCount, projectId, tableData]) => {
        this.isNotLinkedTable.set(!(!!blazeTable?.projectId && blazeTable.projectId !== projectId));
        this.projectId = projectId;
        this.table = {
          ...(blazeTable || DEFAULT_BLAZE_TABLE),
          projectId,
        };
        this.tableData = tableData;

        this.chatContextSession = this.tableEditorChatContextProvider.opened(this.table.name);

        if (this.table.id) {
          this.openAiChat();
        } else {
          this.tablesCollService.add(convertBlazeTableToTableInfo(this.table)).subscribe({
            next: (updatedTable) => {
              this.table = convertBlazeTableSchemaToBlazeTable({
                ...updatedTable,
                name: this.table.name || 'New Table',
                projectId: projectId,
              });
              this.tablesCollService.updateOneInCache(convertBlazeTableToTableInfo(this.table));
              this.chatContextSession?.tableRenamed(this.table.name);
              this.table$.next({
                config: { ...this.table },
                data: [...this.tableData],
                pageToken,
                totalCount,
              });
              this.openAiChat();
            },
            error: (error) => this.notificationService.showError(error),
          });
        }

        this.table$ = new BehaviorSubject({
          config: { ...this.table },
          data: [...this.tableData],
          pageToken,
          totalCount,
        });

        this.refreshWhenAssistantChangesTheTable(this.chatContextSession);
      });
  }

  ngOnDestroy(): void {
    this.chatContextSession?.closed();
    this.tablesCollService.getWithQuery({ projectId: this.projectId }).subscribe(() => {
      this.datasetRowsService.notifyDatasetsChanged([this.table.id]);
    });
    this.triggersDialogRef?.close();
    this.filterDialogRef?.close();
    this.indexesDialogRef?.close();
  }

  dataReload(config?: BlazeTable): void {
    this.refreshDataset.emit(config ?? this.blazeTable.config);
    this.refreshInProgress.set(true);

    // This is mechanism to reload data after data generation - we skip what we have and wait for new data
    this.dataConnectorsStore.previewData$
      .pipe(
        skip(1),
        take(1),
        concatLatestFrom(() => [
          this.dataConnectorsStore.blazeTable$,
          this.dataConnectorsStore.blazeTablePageToken$,
          this.dataConnectorsStore.blazeTableTotalCount$,
        ]),
        untilDestroyed(this),
        finalize(() => this.refreshInProgress.set(false)),
      )
      .subscribe(([data, config, pageToken, totalCount]) => {
        if (!config) {
          return;
        }

        this.filters.set(undefined);
        this.table$.next({ config, data, pageToken, totalCount });
      });
  }

  showPreviousPage(): void {
    this.checkBeforeClose().subscribe((confirm) => {
      if (confirm) {
        return this.showPrevPage.emit();
      }
    });
  }

  clearData(): void {
    this.dialogService
      .openConfirmDialog({
        title: 'Clear Data',
        description: 'Are you sure you want remove all data from this table?',
        cancelLabel: 'Cancel',
        confirmLabel: 'Yes, delete',
      })
      .afterClosed()
      .subscribe((confirmation) => {
        if (confirmation) {
          this.datasetRowsService
            .delete(this.table.id, {
              successMsg: 'Data was successfully removed',
              ignoreResult: true,
            })
            .subscribe(() => {
              this.filters.set(undefined);
              this.datasetsApiService
                .postGetPaginated(
                  this.table.id,
                  {
                    pagination: {
                      maxCount: 50,
                      returnTotalCount: true,
                    },
                    filter: null,
                  },
                  false,
                )
                .subscribe(({ data, pagination }) => {
                  this.tableData = data || this.tableData;
                  this.table$.next({
                    config: { ...this.table },
                    data: [...this.tableData],
                    pageToken: null,
                    totalCount: pagination.totalCount ?? 0,
                  });
                });
            });
        }
      });
  }

  close(blazePanel: PanelComponent): void {
    this.checkBeforeClose().subscribe((confirm) => {
      if (confirm) {
        blazePanel.close();
      }
    });
  }

  exportToCSV(): void {
    if (this.table?.id) {
      this.blazeTable.exportToCSV();
    }
  }

  importCSV(): void {
    this.fileInputRef.nativeElement.click();
  }

  fileSelected(fileInput: EventTarget | null): void {
    const fileInputElement = fileInput as HTMLInputElement;
    const file = fileInputElement?.files?.[0];
    fileInputElement.value = '';
    if (!file) {
      return;
    }
    this.blazeTable.importCSV(file);
  }

  renameTable($event: any): void {
    const name = $event.target.value.trim();
    if (!name) {
      this.notificationService.error('Table name is required');
      return;
    }
    this.table = { ...this.table, name };
    this.chatContextSession?.tableRenamed(name);
    this.blazeTable.renameTable(name);
  }

  enableAccessLogging(): void {
    this.tablesApiService
      .enableAccessLogging(this.table.id)
      .pipe(
        catchError((err) => {
          this.notificationService.showError(err);
          return EMPTY;
        }),
      )
      .subscribe((table) => {
        if (table?.accessLog) {
          this.table = { ...this.table, accessLog: { ...table.accessLog } };
          this.cdr.markForCheck();
        }
      });
  }

  enableTracking(): void {
    this.tablesApiService
      .enableTracking(this.table.id)
      .pipe(
        catchError((err) => {
          this.notificationService.showError(err);
          return EMPTY;
        }),
      )
      .subscribe(() => this.showPreviousPage());
  }

  disableAccessLogging(): void {
    this.dialogService
      .openConfirmDialog({
        confirmLabel: 'Yes, disable',
        description: 'Disabling this will permanently stop all access logging for this table.',
        title: 'Disable Access Logging Table',
        headerIcon: 'icon_access_logging_stop_16',
        headerIconColor: 'text-primary',
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.tablesApiService
            .disableAccessLogging(this.table.id)
            .pipe(
              catchError((err) => {
                this.notificationService.showError(err);
                return EMPTY;
              }),
            )
            .subscribe((table) => {
              if (table) {
                this.table = { ...this.table, accessLog: table.accessLog };
                this.cdr.markForCheck();
              }
            });
        }
      });
  }

  disableTracking(): void {
    this.dialogService
      .openConfirmDialog({
        confirmLabel: 'Yes, disable',
        description:
          'Disabling this will remove related change tracking table and permanently stop all tracking for this table.',
        title: 'Disable Change Tracking Table',
        headerIcon: 'icon_tracking_stop_16',
        headerIconColor: 'text-primary',
      })
      .afterClosed()
      .subscribe((result) => {
        if (result && this.table.changeTracking?.datasetId) {
          this.tablesCollService.delete(this.table.changeTracking.datasetId).subscribe(() => {
            this.table = { ...this.table };
            delete this.table.changeTracking;
          });
        }
      });
  }

  openDataGenerationPanel(): void {
    this.dataGenerationPanelVisible = true;
  }

  openFilterMenu(): void {
    if (this.filterDialogRef) {
      return;
    }
    const initialFilter = (this.filters() || {
      nodeType: QueryNodeType.Logical,
      logicalType: LogicalType.And,
      children: [],
    }) as LogicalQueryNode | ComparisonQueryNode;
    const setFilterOutput = (filter: LogicalQueryNode | undefined): void => {
      this.filters.set(filter);
      this.blazeTable.filter(this.filters());
    };
    const data: BlazeTableFilterDialogInput = {
      initialFilter,
      blazeTableConfig$: this.table$.pipe(map((table) => table.config)),
      setFilterOutput,
    };
    const dialogRef = this.dialog.open<
      BlazeTableFilterDialogComponent,
      BlazeTableFilterDialogInput
    >(BlazeTableFilterDialogComponent, {
      hasBackdrop: false,
      panelClass: ['bd', 'bd-dialog', 'bd-dialog-shadow', 'visible-overflow'],
      data,
      width: '515px',
      position: { top: '105px', right: '54px' },
    });
    const dialog = this.dialogService.decorateDialogOpen(dialogRef);
    this.repositionDialogWhenNeeded(dialog);
    dialog
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.filterDialogRef = undefined;
      });

    this.filterDialogRef = dialog;
  }

  protected isCopyDataOptionVisible(): boolean {
    return (
      !!this.table.id &&
      !this.table.isChangeTrackingTable &&
      !this.table.systemTableType &&
      !this.table.isAuditTable &&
      !!this.sourceEnvironment()
    );
  }

  protected onCopyDataFromEnvironment(sourceEnvironment: EnvironmentName | undefined): void {
    if (!sourceEnvironment) {
      console.warn(`Unable to copy the data. Source environment is unknown`, sourceEnvironment);
      return;
    }

    const data: CopyDataDialogInput = {
      currentEnvironment: sourceEnvironment === 'Development' ? 'Production' : 'Development',
      sourceEnvironment,
    };
    this.dialog
      .open(BlazeTableCopyDataDialogComponent, {
        hasBackdrop: true,
        panelClass: ['bd', 'bd-dialog'],
        data,
        width: '375px',
      })
      .afterClosed()
      .subscribe((confirmation) => {
        if (confirmation) {
          this.blazeTable.copyDataFromEnvironment(sourceEnvironment);
        }
      });
  }

  protected onDuplicateTable(): void {
    this.dialog
      .open(DuplicateBlazeTableDialogComponent, {
        hasBackdrop: true,
        panelClass: ['bd', 'bd-dialog'],
        data: `${this.table.name} Copy`,
        width: '375px',
      })
      .afterClosed()
      .subscribe((confirmation) => {
        if (confirmation) {
          this.tablesApiService
            .copyTable(this.table.id, confirmation.name, confirmation.copyData, this.projectId)
            .subscribe(() => {
              this.showPreviousPage();
            });
        }
      });
  }

  protected openIndexesDialog(): void {
    const dialog = this.dialog.open(BlazeTableIndexesDialogComponent, {
      hasBackdrop: false,
      panelClass: ['bd', 'bd-dialog', 'bd-dialog-shadow', 'visible-overflow'],
      data: {},
      width: '315px',
      position: { top: '105px', right: '54px' },
    });
    this.indexesDialogRef = this.dialogService.decorateDialogOpen(dialog);
    this.indexesDialogRef.componentInstance.blazeTableConfig$ = this.table$.pipe(
      map((table) => table.config),
    );
    this.repositionDialogWhenNeeded(dialog);
  }

  protected configTriggers(): void {
    const selectedRows = Object.keys(this.blazeTable.selectedRows)
      .map((id) => this.tableData.find((row) => row._id === id))
      .filter(isDefined);

    const inputData = selectedRows.length ? { input1: selectedRows } : undefined;
    this.triggersDialogRef = this.dialog.open<
      BlazeTableTriggersDialogComponent,
      BlazeTableTriggersDialogData,
      BlazeTableTriggersDialogResult
    >(BlazeTableTriggersDialogComponent, {
      hasBackdrop: true,
      panelClass: ['bd', 'bd-dialog'],
      data: { tableId: this.table.id, inputData },
      width: '400px',
    });
    this.triggersDialogRef
      .afterClosed()
      .pipe(switchMap((result) => result?.table$ || of(null)))
      .subscribe((table) => {
        if (!table) {
          return;
        }
        this.table = { ...this.table, triggers: table.triggers };
        this.cdr.markForCheck();
      });
  }

  private checkBeforeClose(): Observable<boolean> {
    return this.blazeTable.actionsInProgress$.pipe(
      first(),
      switchMap((actionsInProgress) => {
        const waitForCompletion$ = this.blazeTable.actionsInProgress$.pipe(
          filter((inProgress) => !inProgress),
          take(1),
        );

        const waitWithOptionalDialog$ = actionsInProgress
          ? race(
              waitForCompletion$.pipe(map(() => false as const)),
              timer(500).pipe(map(() => true as const)),
            ).pipe(
              take(1),
              switchMap((dialogNeeded) => {
                if (!dialogNeeded) {
                  return of(undefined);
                }
                const pleaseWaitDialogRef = this.dialogService.openPleaseWaitDialog(
                  {
                    title: 'Closing in progress',
                    description:
                      'Some actions are still running in the background.\n\nThe table will close automatically once they finish.',
                  },
                  { disableClose: true },
                );
                return waitForCompletion$.pipe(tap(() => pleaseWaitDialogRef.close()));
              }),
            )
          : of(undefined);

        return waitWithOptionalDialog$.pipe(
          switchMap(() => {
            let title = '';
            let description = '';

            if (this.blazeTable.rows.filter((r) => !r?._id).length) {
              title = 'You have not saved the records';
              description =
                'Are you sure you want to close the Blaze Table before inserting records?';
            } else if (
              Object.values(this.blazeTable.errorFields).some((v) => Object.values(v).length > 0)
            ) {
              title = 'You have unsaved records';
              description =
                'Are you sure you want to close Blaze Table before updating the records?';
            }

            if (!title) {
              return of(true);
            }

            return this.dialogService
              .openConfirmDialog({
                title,
                description,
                confirmLabel: 'Yes, close',
                cancelLabel: 'No, wait',
              })
              .afterClosed()
              .pipe(map((result) => result === true));
          }),
        );
      }),
    );
  }

  // Reposition when blaze ai panel is open
  private repositionDialogWhenNeeded(dialogRef: MatDialogRef<any>): void {
    afterNextRender(
      () => {
        const hostRect = this.elRef.nativeElement.getBoundingClientRect();
        const panelRect = dialogRef.componentRef?.location.nativeElement.getBoundingClientRect();
        if (panelRect && hostRect && panelRect.right > hostRect.right) {
          dialogRef.updatePosition({
            top: '105px',
            right: `${panelRect.right - hostRect.right + 130}px`,
          });
        }
      },
      { injector: this.injector },
    );
  }

  private openAiChat(): void {
    if (!this.projectId || !this.table.id) {
      return;
    }

    this.chatFacade.openChat(this.projectId);
  }

  private refreshWhenAssistantChangesTheTable(session: TableEditorChatContextSession): void {
    session.changedTableIds$
      .pipe(
        filter((tableId) => !!this.table.id && tableId === this.table.id),
        switchMap(() => this.tablesCollService.entities$.pipe(first())),
        untilDestroyed(this),
      )
      .subscribe((tables) => {
        const refreshed = tables.find(({ id }) => id === this.table.id);
        if (!refreshed) {
          return;
        }

        if (refreshed.name !== this.table.name) {
          session.tableRenamed(refreshed.name);
        }
        this.table = {
          ...this.table,
          columns: refreshed.columns,
          displayColumn: refreshed.displayColumn,
          name: refreshed.name,
        };
        this.table$.next({ ...this.table$.value, config: { ...this.table } });
        this.datasetRowsService.notifyDatasetsChanged([this.table.id]);
        this.cdr.markForCheck();

        this.dataReload(this.table);
      });
  }
}
